/**
 * 63.11 step 1 (ARCHITECTURE §5.18, craterlets; moved out of core/fineTerrain.ts for 63.14 2b's terraces): a per-planet
 * cell index over the person-scale ground's bumps — the digs, the craterlets, the terraces — so a fine-ground lookup reads
 * one cell's few entries, not every bump on the planet.
 */
import type { Vec3 } from '../types';

/**
 * 63.11 step 1 (ARCHITECTURE §5.18, craterlets): a per-planet cell index over the person-scale ground's bumps (the digs; the
 * craterlets next) — a fine-ground lookup reads one cell's few entries, not every bump on the planet. Cells are cubes in
 * the planet's frame, `2 × reach` a side; each bump is filed in every cell its reach's box meets, so a point's own cell
 * holds every bump that reaches it, in the list's order (ascending indices). Memoised on the list's identity and the
 * reach (the list is a new array whenever a bump is added, so it is rebuilt once a change).
 */
export interface BumpIndex { at(ux: number, uy: number, uz: number): readonly number[] }
const NO_BUMPS: readonly number[] = [];
/** Cells a side of the planet's frame each way from its centre the packed key holds (a planet up to 2¹⁶ cells across). */
const CELL_SPAN = 1 << 16;
const bumpIndexes = new WeakMap<readonly unknown[], { radius: number; reach: number; index: BumpIndex }>();
export function bumpIndex(list: ReadonlyArray<{ dir: Vec3 }>, radius: number, reach: number): BumpIndex {
  const kept = bumpIndexes.get(list);
  if (kept && kept.radius === radius && kept.reach === reach) return kept.index;
  const size = 2 * reach, cells = new Map<number, number[]>();
  // (a cell's key: its three coordinates, offset into 0 … 2·CELL_SPAN, packed in one safe integer)
  const key = (ix: number, iy: number, iz: number) => ((ix + CELL_SPAN) * 2 * CELL_SPAN + (iy + CELL_SPAN)) * 2 * CELL_SPAN + (iz + CELL_SPAN);
  for (let i = 0; i < list.length; i++) {
    const d = list[i].dir, l = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z) || 1;
    const x = (d.x / l) * radius, y = (d.y / l) * radius, z = (d.z / l) * radius;
    for (let ix = Math.floor((x - reach) / size); ix <= Math.floor((x + reach) / size); ix++)
      for (let iy = Math.floor((y - reach) / size); iy <= Math.floor((y + reach) / size); iy++)
        for (let iz = Math.floor((z - reach) / size); iz <= Math.floor((z + reach) / size); iz++) {
          const kk = key(ix, iy, iz), c = cells.get(kk);
          if (c) { if (c[c.length - 1] !== i) c.push(i); } else cells.set(kk, [i]);
        }
  }
  const index: BumpIndex = { at: (ux, uy, uz) => cells.get(key(Math.floor((ux * radius) / size), Math.floor((uy * radius) / size), Math.floor((uz * radius) / size))) ?? NO_BUMPS };
  bumpIndexes.set(list, { radius, reach, index });
  return index;
}
